import type { ReactElement } from 'react';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toneColor, type Tone } from './tone';

export type StatTileProps = {
  count: number | string;
  title: string;
  note: string;
  icon: ReactElement;
  tone: Tone;
  /** a click goes to this page, or runs this */
  to?: string;
  onClick?: () => void;
  selected?: boolean;
  /** a smaller number, for money like "JOD 148,250" */
  compact?: boolean;
};

/** A big number with an icon, a title and a small note. The tiles at the top of Home and Shipments. */
export function StatTile({ count, title, note, icon, tone, to, onClick, selected, compact }: StatTileProps) {
  const link = to ? { component: RouterLink, to } : { onClick };
  return (
    <Card sx={{ borderColor: selected ? 'primary.main' : undefined }}>
      <CardActionArea {...link} sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              placeItems: 'center',
              width: 48,
              height: 48,
              borderRadius: 2,
              flexShrink: 0,
              color: toneColor(tone),
              bgcolor: `color-mix(in srgb, ${toneColor(tone)} 14%, transparent)`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: compact ? 20 : 28, fontWeight: 750, lineHeight: 1.1 }}>{count}</Typography>
            <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {note}
            </Typography>
          </Box>
          {to && <ChevronRightIcon color="disabled" sx={{ display: { xs: 'none', sm: 'block' } }} />}
        </Stack>
      </CardActionArea>
    </Card>
  );
}
